// alterLogin — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-login' })` op (DSE2 §3.F1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice F1 — Logins / Users / Roles UI.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, LoginChange } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AlterLoginArgs {
  readonly loginName: string;
  readonly quoted?: boolean;
  readonly change: LoginChange;
}

export function buildAlterLoginEdit(args: AlterLoginArgs): DdlEdit {
  return {
    op: 'alter-login',
    loginName: args.loginName,
    ...(args.quoted ? { quoted: true } : {}),
    change: args.change,
  };
}

export async function commitAlterLogin(
  ctx: VqeDdlEditContext,
  args: AlterLoginArgs,
): Promise<void> {
  const edit = buildAlterLoginEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAlterLoginSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function getAlterLoginUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') return 'SQLite has no auth model.';
  if (engine === 'postgres') return 'PostgreSQL has no LOGIN — use ALTER ROLE.';
  return `${engine} has no server-level LOGIN — use ALTER USER instead.`;
}

export function isNonEmptyLoginChange(change: LoginChange): boolean {
  return Boolean(
    change.mssqlPassword
      || change.mssqlOldPassword
      || change.mssqlDefaultDatabase
      || change.mssqlEnable !== undefined
      || change.mssqlName,
  );
}

export function validateAlterLoginForEngine(
  engine: SqlEngine,
  args: AlterLoginArgs,
): string | null {
  if (!isAlterLoginSupported(engine)) {
    return getAlterLoginUnsupportedTooltip(engine);
  }
  if (args.loginName.trim().length === 0) {
    return 'Login name is required.';
  }
  if (!isNonEmptyLoginChange(args.change)) {
    return 'Pick at least one change (password, default database, enable/disable, or rename).';
  }
  if (args.change.mssqlOldPassword && !args.change.mssqlPassword) {
    return 'OLD_PASSWORD requires a new password.';
  }
  return null;
}
